import React, { useEffect, useState } from "react";
import { CalendarDays, Phone, MapPin, Copy, MessageCircle, Share2 } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

/** Prominent banner shown on the homepage + Hijama Atlas page.
 *  Displays the upcoming 3 Hijama Sunnah days (17, 19, 21 of the Hijri month),
 *  converts them to Gregorian, and provides:
 *   - WhatsApp booking button (deep-link to clinic number)
 *   - "Copy reminder" button (so the owner can paste it on FB/IG/WA in one tap)
 */
const HijamaSunnahDaysCard = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const { lang, t } = useLanguage();

  useEffect(() => {
    api
      .get("/hijama/sunnah-days", { params: { lang } })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [lang]);

  if (!data || !data.days?.length) return null;

  const whatsappBookingMsg = encodeURIComponent(
    `Assalāmu ʿalaykum, I would like to book a Hijama session at Islamic Cure & Care. ` +
      `My preferred Sunnah day is ${data.days[0].gregorian_label}. JazākAllāhu khayrā.`
  );
  const waBook = `https://wa.me/${data.clinic_whatsapp_e164.replace("+", "")}?text=${whatsappBookingMsg}`;

  // Universal "Share on WhatsApp" — uses wa.me/?text= (no phone number) so
  // the receiver picks the contact from their address book. Reuses the same
  // monthly reminder text that the "Copy reminder" button copies.
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const waShareMsg = encodeURIComponent(`${data.monthly_reminder}\n\n${shareUrl}`);
  const waShare = `https://wa.me/?text=${waShareMsg}`;

  const copyReminder = async () => {
    try {
      await navigator.clipboard.writeText(data.monthly_reminder);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // ignore — clipboard not available
    }
  };

  return (
    <section
      data-testid="hijama-sunnah-days-card"
      className="rounded-3xl overflow-hidden border border-gold/40 bg-gradient-to-br from-forest via-[#1F4030] to-[#0F2A1F] text-sand shadow-xl"
    >
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-5 h-5 text-gold" />
          <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">
            {t("sunnah_days_pill")}
          </p>
        </div>
        <p className="text-sand/85 text-sm mb-5 italic leading-relaxed">
          {t("sunnah_days_hadith")}
          <br />
          <span className="text-gold/80 text-[11px] not-italic">{t("sunnah_days_hadith_ref")}</span>
        </p>

        <div className={`grid ${compact ? "grid-cols-3" : "grid-cols-1 sm:grid-cols-3"} gap-3 mb-5`}>
          {data.days.slice(0, 3).map((d, i) => (
            <div
              key={d.gregorian_iso}
              data-testid={`sunnah-day-${i}`}
              className="bg-sand/10 backdrop-blur rounded-2xl p-4 border border-gold/30 hover:border-gold/60 transition"
            >
              <p className="text-[10px] uppercase tracking-wider text-gold/90 font-semibold mb-1">
                {d.weekday}
              </p>
              <p className="font-heading text-2xl text-sand font-semibold leading-none">
                {d.gregorian_label.split(" ").slice(1, 3).join(" ")}
              </p>
              <p className="text-[11px] text-sand/70 mt-2 arabic" dir="rtl">
                {d.hijri_label}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <a
            data-testid="book-hijama-whatsapp"
            href={waBook}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gold hover:bg-[#A07A2F] text-ink font-semibold px-5 py-2.5 rounded-full transition shadow-md text-sm"
          >
            <MessageCircle className="w-4 h-4" /> {t("book_on_whatsapp")}
          </a>
          <a
            data-testid="book-hijama-phone"
            href={`tel:${data.clinic_whatsapp_e164}`}
            className="inline-flex items-center gap-2 bg-sand/15 hover:bg-sand/25 text-sand border border-sand/30 px-5 py-2.5 rounded-full transition text-sm"
            style={{ direction: "ltr" }}
          >
            <Phone className="w-4 h-4" /> {data.clinic_phone_display}
          </a>
          {!compact && (
            <>
              <a
                data-testid="share-sunnah-days-whatsapp"
                href={waShare}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366]/90 hover:bg-[#25D366] text-white border border-white/20 px-4 py-2.5 rounded-full transition text-xs ml-auto"
              >
                <Share2 className="w-3.5 h-3.5" /> {t("share_on_whatsapp")}
              </a>
              <button
                type="button"
                onClick={copyReminder}
                data-testid="copy-monthly-reminder"
                className="inline-flex items-center gap-2 bg-sand/10 hover:bg-sand/20 text-sand border border-sand/20 px-4 py-2.5 rounded-full transition text-xs"
                title={t("copy_reminder")}
              >
                {copied ? (
                  t("copied")
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" /> {t("copy_reminder")}
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {!compact && (
          <div className="mt-4 pt-4 border-t border-sand/20 flex items-center gap-2 text-[11px] text-sand/70">
            <MapPin className="w-3.5 h-3.5 text-gold" />
            <span>
              {data.clinic_address} — {t("male_female_therapists")}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default HijamaSunnahDaysCard;
